import React from 'react'
import { Bold, Link, LinkVariant, Space } from '@gousto-internal/citrus-react'

import type { RichTextContent } from '../../types/contentstack'

type SpaceSize = React.ComponentProps<typeof Space>['size']

type RteNode = {
  type?: string
  uid?: string
  text?: string
  attrs?: {
    url?: string
    target?: string
    [key: string]: unknown
  }
  children?: RteNode[]
  bold?: boolean
  italic?: boolean
  underline?: boolean
  strikethrough?: boolean
  superscript?: boolean
  subscript?: boolean
}

type RenderNodeArgs = {
  children: React.ReactNode
  key: React.Key
  node: RteNode
}

type RenderParagraphArgs = RenderNodeArgs & {
  index: number
}

export type RichTextProps = {
  content?: RichTextContent
  paragraphGapSize?: SpaceSize
  renderParagraph?: (args: RenderParagraphArgs) => React.ReactNode
  renderLink?: (args: RenderNodeArgs & { href: string; target?: string }) => React.ReactNode
  renderUnorderedList?: (args: RenderNodeArgs) => React.ReactNode
  renderOrderedList?: (args: RenderNodeArgs) => React.ReactNode
  renderListItem?: (args: RenderNodeArgs) => React.ReactNode
}

function isLeaf(node: RteNode): node is RteNode & { text: string } {
  return typeof node.text === 'string'
}

function hasRichTextContent(node: RteNode): boolean {
  if (isLeaf(node)) {
    return node.text.trim().length > 0
  }

  return (node.children ?? []).some(hasRichTextContent)
}

function RichTextLeaf({ node }: { node: RteNode & { text: string } }) {
  let content: React.ReactNode = node.text

  if (node.bold) {
    content = <Bold>{content}</Bold>
  }

  if (node.italic) {
    content = <em>{content}</em>
  }

  if (node.underline) {
    content = <u>{content}</u>
  }

  if (node.strikethrough) {
    content = <s>{content}</s>
  }

  if (node.superscript) {
    content = <sup>{content}</sup>
  }

  if (node.subscript) {
    content = <sub>{content}</sub>
  }

  return <>{content}</>
}

function renderDefaultLink({
  children,
  key,
  href,
  target,
}: RenderNodeArgs & { href: string; target?: string }) {
  return (
    <Link key={key} variant={LinkVariant.None} href={href} target={target}>
      {children}
    </Link>
  )
}

function renderDefaultList({ children, key }: RenderNodeArgs, type: 'ul' | 'ol') {
  return React.createElement(type, { key }, children)
}

function renderDefaultListItem({ children, key }: RenderNodeArgs) {
  return <li key={key}>{children}</li>
}

function RichTextNodes({
  nodes,
  renderLink = renderDefaultLink,
  renderUnorderedList = (args) => renderDefaultList(args, 'ul'),
  renderOrderedList = (args) => renderDefaultList(args, 'ol'),
  renderListItem = renderDefaultListItem,
}: Pick<
  RichTextProps,
  'renderLink' | 'renderUnorderedList' | 'renderOrderedList' | 'renderListItem'
> & {
  nodes: RteNode[]
}) {
  return (
    <>
      {nodes.map((node, index) => {
        const key = node.uid ?? `node-${index}`

        if (isLeaf(node)) {
          return <RichTextLeaf key={key} node={node} />
        }

        const children = (
          <RichTextNodes
            nodes={node.children ?? []}
            renderLink={renderLink}
            renderUnorderedList={renderUnorderedList}
            renderOrderedList={renderOrderedList}
            renderListItem={renderListItem}
          />
        )

        if (node.type === 'a' && node.attrs?.url) {
          return renderLink({
            children,
            key,
            node,
            href: node.attrs.url,
            target: node.attrs.target,
          })
        }

        if (node.type === 'ul') {
          return renderUnorderedList({ children, key, node })
        }

        if (node.type === 'ol') {
          return renderOrderedList({ children, key, node })
        }

        if (node.type === 'li') {
          return renderListItem({ children, key, node })
        }

        return <React.Fragment key={key}>{children}</React.Fragment>
      })}
    </>
  )
}

export function getRenderableRichTextBlocks(content?: RichTextContent) {
  if (!content) {
    return []
  }

  const root = content as RteNode

  return (root.children ?? []).filter((block) => {
    if (!hasRichTextContent(block)) {
      return false
    }

    return block.type === 'p' || block.type === 'ul' || block.type === 'ol'
  })
}

export function RichText({
  content,
  paragraphGapSize = 4,
  renderParagraph,
  renderLink,
  renderUnorderedList,
  renderOrderedList,
  renderListItem,
}: RichTextProps) {
  const blocks = getRenderableRichTextBlocks(content)

  if (blocks.length === 0) {
    return null
  }

  return (
    <>
      {blocks.map((block, index) => {
        const key = block.uid ?? `block-${index}`
        const children = (
          <RichTextNodes
            nodes={block.children ?? []}
            renderLink={renderLink}
            renderUnorderedList={renderUnorderedList}
            renderOrderedList={renderOrderedList}
            renderListItem={renderListItem}
          />
        )

        const contentNode =
          block.type === 'p'
            ? renderParagraph?.({ children, key, node: block, index }) ?? (
                <React.Fragment key={key}>{children}</React.Fragment>
              )
            : block.type === 'ul'
            ? (renderUnorderedList ?? ((args) => renderDefaultList(args, 'ul')))({
                children,
                key,
                node: block,
              })
            : (renderOrderedList ?? ((args) => renderDefaultList(args, 'ol')))({
                children,
                key,
                node: block,
              })

        return (
          <React.Fragment key={key}>
            {index > 0 && <Space size={paragraphGapSize} />}
            {contentNode}
          </React.Fragment>
        )
      })}
    </>
  )
}
